import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Avatar,
  Box,
  Button,
  Grid,
  GridItem,
  HStack,
  Icon,
  Stack,
  Text,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Tooltip } from "../components/Tip";
import { FiCalendar, FiCheck, FiClock, FiDollarSign, FiGrid, FiLink } from 'react-icons/fi';
import { format, startOfWeek, addDays, parseISO } from 'date-fns';
import { CalEmpty, CalError, CalLoading, CalPageHeader, CalSectionHeading, CalSurface, alpha, useCalTheme } from '../components/ui';
import { BookingFact, BookingShell, BookingSteps, SelectedTimeCard, WeekSlotPicker, locationIcon, locationWords } from './BookingParts';
import {
  PUBLIC_CALENDAR_QUERY,
  PUBLIC_EVENT_TYPES_QUERY,
  AVAILABLE_TIME_SLOTS_QUERY,
} from '../queries';

// ==================== TYPES ====================

interface PublicCalendar {
  id: string;
  name: string;
  description?: string;
  color: string;
  bookingPageSlug: string;
  bookingPageTitle?: string;
  bookingPageDescription?: string;
  bookingPageLogo?: string;
  bookingPageProfilePhoto?: string;
}

interface BookableEventType {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  isPaid: boolean;
  price?: number;
  currency?: string;
  locationType: string;
  location?: string;
  meetingLink?: string;
  isActive: boolean;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
}

// ==================== COMPONENT ====================

export const PublicBookingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const toast = useToast();
  const t = useCalTheme();

  // Query for calendar details
  const { data: calendarData, loading: calendarLoading, error: calendarError, refetch: refetchCalendar } = useQuery(
    PUBLIC_CALENDAR_QUERY,
    {
      variables: { slug },
      skip: !slug,
    }
  );

  // Query for event types
  const { data: eventTypesData, loading: eventTypesLoading, error: eventTypesError, refetch: refetchEventTypes } = useQuery(
    PUBLIC_EVENT_TYPES_QUERY,
    {
      variables: { calendarSlug: slug },
      skip: !slug,
    }
  );

  // Utility function to generate slug from event type name (matching backend logic)
  const generateEventTypeSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  // Auto-select event type from query parameter or select first event type
  useEffect(() => {
    if (!eventTypesData?.publicBookableEventTypes || hasAutoSelected) return;

    const eventTypes = eventTypesData.publicBookableEventTypes as BookableEventType[];
    if (eventTypes.length === 0) return;

    // Check for query parameters
    const eventTypeSlug = searchParams.get('eventType');
    const eventTypeId = searchParams.get('id');

    if (eventTypeId) {
      // Direct ID match (most reliable)
      const matchedEvent = eventTypes.find(et => et.id === eventTypeId);
      if (matchedEvent) {
        console.log('🎯 Auto-selected event type by ID:', matchedEvent.name);
        setSelectedEventType(matchedEvent.id);
        setHasAutoSelected(true);
        return;
      }
    }

    if (eventTypeSlug) {
      // Try to match by slug
      const matchedEvent = eventTypes.find(
        et => generateEventTypeSlug(et.name) === eventTypeSlug
      );
      if (matchedEvent) {
        console.log('🎯 Auto-selected event type by slug:', matchedEvent.name);
        setSelectedEventType(matchedEvent.id);
        setHasAutoSelected(true);
        return;
      }
    }

    // No query parameter or no match - auto-select first event type
    console.log('📋 Auto-selecting first event type:', eventTypes[0].name);
    setSelectedEventType(eventTypes[0].id);
    setHasAutoSelected(true);
  }, [eventTypesData, hasAutoSelected, searchParams]);

  // Debug logging
  useEffect(() => {
    console.log('📋 Event Types Query Status:');
    console.log('   Loading:', eventTypesLoading);
    console.log('   Error:', eventTypesError);
    console.log('   Data:', eventTypesData);
    console.log('   Slug:', slug);
    console.log('   Query Params:', {
      eventType: searchParams.get('eventType'),
      id: searchParams.get('id'),
    });
  }, [eventTypesLoading, eventTypesError, eventTypesData, slug, searchParams]);

  // Query for available time slots (only when event type selected)
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
  const weekEnd = addDays(weekStart, 6);

  const { data: timeSlotsData, loading: timeSlotsLoading, error: timeSlotsError, refetch: refetchTimeSlots } = useQuery(
    AVAILABLE_TIME_SLOTS_QUERY,
    {
      variables: {
        calendarId: calendarData?.publicCalendar?.id,
        eventTypeId: selectedEventType,
        startDate: weekStart.toISOString(),
        endDate: weekEnd.toISOString(),
      },
      skip: !calendarData?.publicCalendar?.id || !selectedEventType,
      fetchPolicy: 'network-only', // Always fetch fresh data, don't use cache
    }
  );

  // Refetch time slots when event type changes
  useEffect(() => {
    if (selectedEventType && calendarData?.publicCalendar?.id) {
      console.log('🔄 Event type changed, refetching time slots for:', selectedEventType);
      refetchTimeSlots();
    }
  }, [selectedEventType, calendarData?.publicCalendar?.id, refetchTimeSlots]);

  // ==================== HANDLERS ====================

  const handleEventTypeSelect = (eventTypeId: string) => {
    console.log('📋 Selecting event type:', eventTypeId);
    setSelectedEventType(eventTypeId);
    setSelectedTime(null); // Clear selected time when changing event type
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (timeSlot: TimeSlot) => {
    setSelectedTime(timeSlot);
  };

  const handleBooking = () => {
    if (!selectedEventType || !selectedTime) return;

    // Navigate to booking form with selected details
    navigate(`/book/${slug}/confirm`, {
      state: {
        eventTypeId: selectedEventType,
        startTime: selectedTime.startTime,
        endTime: selectedTime.endTime,
      },
    });
  };

  const generateEventTypeSlugForLink = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const copyBookingLink = async () => {
    if (!selectedEventType || !selectedEventTypeDetails || !slug) return;

    const eventTypeSlug = generateEventTypeSlugForLink(selectedEventTypeDetails.name);
    const baseUrl = window.location.origin;
    const directLink = `${baseUrl}/book/${slug}?eventType=${eventTypeSlug}&id=${selectedEventType}`;

    try {
      await navigator.clipboard.writeText(directLink);
      setLinkCopied(true);
      toast({
        title: 'Link copied!',
        description: 'Direct booking link for this event type copied to clipboard',
        status: 'success',
        duration: 2000,
      });

      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // ==================== LOADING & ERROR STATES ====================

  if (calendarLoading) {
    return (
      <BookingShell>
        <CalLoading label="Loading booking page…" cards={2} columns={{ base: 1, lg: 2 }} />
      </BookingShell>
    );
  }

  // A failed request is not "not found": say which, and offer to try again.
  if (calendarError) {
    return (
      <BookingShell>
        <CalError title="This booking page couldn't be loaded" error={calendarError} onRetry={() => refetchCalendar()} />
      </BookingShell>
    );
  }

  if (!calendarData?.publicCalendar) {
    return (
      <BookingShell>
        <CalEmpty icon={FiCalendar} title="Booking page not found">
          This booking page doesn't exist or is no longer taking bookings. Check the link you were sent, or ask the person who shared it.
        </CalEmpty>
      </BookingShell>
    );
  }

  const calendar = calendarData.publicCalendar as PublicCalendar;
  const eventTypes = ((eventTypesData?.publicBookableEventTypes || []) as BookableEventType[])
    .filter((et) => et.isActive !== false);
  const timeSlots = (timeSlotsData?.availableTimeSlots || []) as TimeSlot[];

  // Group time slots by day
  const slotsByDay: Record<string, TimeSlot[]> = {};
  timeSlots.forEach((slot) => {
    const date = format(parseISO(slot.startTime), 'yyyy-MM-dd');
    if (!slotsByDay[date]) {
      slotsByDay[date] = [];
    }
    slotsByDay[date].push(slot);
  });

  // Find selected event type details
  const selectedEventTypeDetails = eventTypes.find((et) => et.id === selectedEventType);
  const hostPhoto = calendar.bookingPageProfilePhoto || calendar.bookingPageLogo;

  // ==================== RENDER ====================

  return (
    <BookingShell maxW="container.xl">
      <Stack spacing={{ base: 4, md: 6 }}>
        <CalPageHeader
          icon={FiCalendar}
          eyebrow="Book a time"
          title={calendar.bookingPageTitle || calendar.name}
          subtitle={calendar.bookingPageDescription || calendar.description}
          actions={hostPhoto ? (
            <HStack spacing={3}>
              <Avatar size="lg" name={calendar.name} src={hostPhoto} bg={calendar.color} color="white" />
              {calendar.bookingPageTitle && <Text fontSize="md" fontWeight="semibold" color={t.text}>{calendar.name}</Text>}
            </HStack>
          ) : undefined}
        />

        <BookingSteps done={selectedTime ? 2 : selectedEventType ? 1 : 0} />

        <Grid templateColumns={{ base: '1fr', lg: 'minmax(0, 1fr) minmax(0, 2fr)' }} gap={{ base: 4, md: 6 }}>
          {/* Step 1 — what to book */}
          <GridItem minW={0}>
            <CalSurface>
              <CalSectionHeading icon={FiGrid} title="1. Choose what to book" why="Tap one to see its free times." />
              {eventTypesLoading ? (
                <CalLoading label="Loading what you can book…" cards={2} columns={{ base: 1 }} />
              ) : eventTypesError ? (
                <CalError title="We couldn't load what you can book" error={eventTypesError} onRetry={() => refetchEventTypes()} />
              ) : eventTypes.length === 0 ? (
                <CalEmpty icon={FiCalendar} title="No event types available right now">
                  Nothing is open for booking on this page at the moment. Please check back later.
                </CalEmpty>
              ) : (
                <Stack spacing={3} role="radiogroup" aria-label="What to book">
                  {eventTypes.map((eventType) => {
                    const chosen = selectedEventType === eventType.id;
                    return (
                      <Tooltip key={eventType.id} label={`Show free times for ${eventType.name}. Nothing is booked yet.`}>
                        <Box
                          as="button"
                          type="button"
                          role="radio"
                          aria-checked={chosen}
                          onClick={() => handleEventTypeSelect(eventType.id)}
                          textAlign="left"
                          w="full"
                          p={4}
                          borderRadius="xl"
                          borderWidth="2px"
                          borderColor={chosen ? t.primary : t.border}
                          bg={chosen ? alpha(t.primary, 0.08) : t.raised}
                          transition="border-color .15s ease, background .15s ease"
                          _hover={{ borderColor: t.primary }}
                          _focusVisible={{ outline: 'none', boxShadow: `0 0 0 3px ${alpha(t.primary, 0.45)}` }}
                        >
                          <HStack justify="space-between" align="start" spacing={3} mb={eventType.description ? 1 : 2}>
                            <Text fontSize="lg" fontWeight="bold" color={t.text} wordBreak="break-word">{eventType.name}</Text>
                            {chosen && <Icon as={FiCheck} color={t.primary} boxSize={6} flexShrink={0} aria-hidden />}
                          </HStack>
                          {eventType.description && (
                            <Text fontSize="md" color={t.muted} mb={3} noOfLines={3}>
                              {eventType.description}
                            </Text>
                          )}
                          <Wrap spacing={2}>
                            <WrapItem><BookingFact icon={FiClock}>{eventType.durationMinutes} min</BookingFact></WrapItem>
                            <WrapItem>
                              <BookingFact icon={FiDollarSign}>
                                {eventType.isPaid && eventType.price ? (
                                  <>
                                    <span>{`$${eventType.price}`}</span>
                                    {eventType.currency && <span> {eventType.currency}</span>}
                                  </>
                                ) : 'Free'}
                              </BookingFact>
                            </WrapItem>
                            <WrapItem>
                              <BookingFact icon={locationIcon(eventType.locationType)} testId="location-icon">
                                {locationWords(eventType.locationType)}
                              </BookingFact>
                            </WrapItem>
                          </Wrap>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Stack>
              )}
            </CalSurface>
          </GridItem>

          {/* Step 2 — when */}
          <GridItem minW={0}>
            {!selectedEventType ? (
              <CalEmpty icon={FiCalendar} title="Choose what to book first">
                Pick one of the options to see the times that are free.
              </CalEmpty>
            ) : (
              <Stack spacing={{ base: 4, md: 6 }}>
                {selectedEventTypeDetails && (
                  <CalSurface>
                    <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} spacing={3}>
                      <Box minW={0}>
                        <Text fontSize="sm" color={t.faint}>You're booking</Text>
                        <Text fontSize="lg" fontWeight="bold" color={t.text} wordBreak="break-word">{selectedEventTypeDetails.name}</Text>
                        <HStack spacing={2} mt={1} flexWrap="wrap">
                          <Text fontSize="md" color={t.muted}>{selectedEventTypeDetails.durationMinutes} minutes</Text>
                          {selectedEventTypeDetails.isPaid && selectedEventTypeDetails.price ? (
                            <Text fontSize="md" color={t.muted}>· {selectedEventTypeDetails.price} {selectedEventTypeDetails.currency}</Text>
                          ) : null}
                        </HStack>
                      </Box>
                      <Stack direction={{ base: 'column', sm: 'row' }} spacing={2} flexShrink={0}>
                        <Tooltip label="Copy a link that opens this page with this option already chosen — handy to send to someone. Nothing is booked.">
                          <Button
                            minH="44px"
                            variant="outline"
                            leftIcon={<Icon as={linkCopied ? FiCheck : FiLink} />}
                            color={linkCopied ? t.success : t.text}
                            borderColor={linkCopied ? t.success : t.borderStrong}
                            onClick={copyBookingLink}
                          >
                            {linkCopied ? 'Link copied' : 'Copy link'}
                          </Button>
                        </Tooltip>
                        <Tooltip label="Go back and choose a different option. Nothing is booked.">
                          <Button minH="44px" variant="ghost" color={t.muted} onClick={() => setSelectedEventType(null)}>
                            Change
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CalSurface>
                )}

                <WeekSlotPicker
                  weekStart={weekStart}
                  selectedTime={selectedTime}
                  slotsByDay={slotsByDay}
                  loading={timeSlotsLoading}
                  error={timeSlotsError}
                  onRetry={() => refetchTimeSlots()}
                  onPrevWeek={() => handleDateSelect(addDays(weekStart, -7))}
                  onNextWeek={() => handleDateSelect(addDays(weekStart, 7))}
                  onPick={(day, slot) => {
                    handleDateSelect(day);
                    handleTimeSelect(slot);
                  }}
                />

                {selectedTime && (
                  <SelectedTimeCard slot={selectedTime} eventType={selectedEventTypeDetails} onContinue={handleBooking} />
                )}
              </Stack>
            )}
          </GridItem>
        </Grid>
      </Stack>
    </BookingShell>
  );
};

export default PublicBookingPage;
